import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AddTaskIcon from '@mui/icons-material/AddTask';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import { auditsApi } from '../services/audits';
import { api } from '../services/api';
import { tasksApi } from '../services/tasks';
import { SuggestionRow } from '../components/strategy/SuggestionRow';
import type { Audit, BrandPerceptionSummary, AgentStatus, BrandStrategy } from '@traaker/shared';

const STATUS_COLORS: Record<string, 'default' | 'info' | 'success' | 'error'> = {
  pending: 'default',
  running: 'info',
  completed: 'success',
  failed: 'error',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'En attente',
  running: 'En cours',
  completed: 'Termine',
  failed: 'Echoue',
};

const AGENT_STATUS_COLORS: Record<AgentStatus, string> = {
  pending: '#A0A4AE',
  running: '#F59E0B',
  completed: '#10B981',
  failed: '#EF4444',
};

const ALIGNMENT_COLORS: Record<string, string> = {
  strong: '#00DDB3',
  partial: '#FF6B35',
  weak: '#FF4444',
  misaligned: '#FF4444',
};

const ALIGNMENT_LABELS: Record<string, string> = {
  strong: 'Fort',
  partial: 'Partiel',
  weak: 'Faible',
  misaligned: 'Desaligne',
};

const LEVEL_LABELS: Record<string, string> = {
  L1: 'L1 — Service + Lieu',
  L2: 'L2 — Offre specifique + Lieu',
  L3: 'L3 — Besoin client + Lieu',
  L4: 'L4 — Proposition de valeur',
  L5: 'L5 — Probleme client specifique',
};

const LEVEL_WEIGHTS: Record<string, number> = {
  L1: 0.05,
  L2: 0.10,
  L3: 0.25,
  L4: 0.30,
  L5: 0.30,
};

// Highlight brand name / aliases in response text
function HighlightedResponse({ text, brandName, aliases }: {
  text: string;
  brandName?: string;
  aliases?: string[];
}) {
  const terms = useMemo(() => {
    const t = [brandName, ...(aliases || [])].filter(Boolean) as string[];
    return t.sort((a, b) => b.length - a.length); // longest first to avoid partial matches
  }, [brandName, aliases]);

  if (!terms.length || !text) {
    return <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{text}</Typography>;
  }

  const regex = new RegExp(`(${terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
  const parts = text.split(regex);

  return (
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
      {parts.map((part, i) =>
        terms.some((t) => t.toLowerCase() === part.toLowerCase()) ? (
          <Box
            key={i}
            component="span"
            sx={{ bgcolor: '#00DDB340', color: '#00DDB3', px: 0.5, borderRadius: 0.5, fontWeight: 700 }}
          >
            {part}
          </Box>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </Typography>
  );
}

function ScoreBar({ score, label }: { score: number; label: string }) {
  const color = score >= 70 ? '#00DDB3' : score >= 40 ? '#FF6B35' : '#FF4444';
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="body2" fontWeight={700} sx={{ color }}>{score}/100</Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={score}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: 'rgba(11, 11, 15, 0.06)',
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 4 },
        }}
      />
    </Box>
  );
}

function SummaryCard({ title, value, suffix, color }: {
  title: string;
  value: string;
  suffix: string;
  color: string;
}) {
  return (
    <Card>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography variant="caption" color="text.secondary">{title}</Typography>
        <Typography variant="h5" fontWeight={700} sx={{ color }}>
          {value}{suffix}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function BrandPerception() {
  const { activeBrand } = useBrand();
  const [audits, setAudits] = useState<Audit[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [selectedAudit, setSelectedAudit] = useState<Audit | null>(null);

  const loadAudits = async () => {
    try {
      const data = await auditsApi.list('brand', activeBrand?.brandId);
      setAudits(data);
    } catch {
      setError('Erreur lors du chargement des audits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setSelectedAudit(null);
    loadAudits();
  }, [activeBrand?.brandId]);

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      await auditsApi.create('brand', {}, activeBrand?.brandId);
      setDialogOpen(false);
      await loadAudits();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la creation');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (selectedAudit) {
    return (
      <BrandPerceptionDetail
        audit={selectedAudit}
        onBack={() => setSelectedAudit(null)}
        onRefresh={async () => {
          const updated = await auditsApi.getById(selectedAudit.auditId);
          if (updated) setSelectedAudit(updated);
        }}
      />
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          Brand Perception
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          Nouvel Audit
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {audits.length === 0 ? (
        <Card>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Aucun audit Brand Perception
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Mesurez ce que les moteurs IA disent de {activeBrand?.name || 'votre marque'} —
              narrative, autorite thematique et recommandation — en interrogeant
              directement les moteurs de recherche IA.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
              Lancer un audit
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Card}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Marque</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Agent</TableCell>
                <TableCell>Score</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {audits.map((audit) => {
                const agentStatus = audit.agentStatuses?.brand_perception || 'pending';
                return (
                  <TableRow
                    key={audit.auditId}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setSelectedAudit(audit)}
                  >
                    <TableCell>
                      {new Date(audit.createdAt).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </TableCell>
                    <TableCell>{activeBrand?.name || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABELS[audit.status] || audit.status}
                        color={STATUS_COLORS[audit.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={`Brand Perception: ${STATUS_LABELS[agentStatus] || agentStatus}`}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: AGENT_STATUS_COLORS[agentStatus] || '#555',
                          }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600}>
                        {audit.brandPerceptionSummary?.overall_brand_perception_score ?? '—'}
                        {audit.brandPerceptionSummary?.overall_brand_perception_score !== undefined && '/100'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nouvel Audit Brand Perception</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body1">
              Analyser la perception IA de <strong>{activeBrand?.name || '—'}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              L'audit interrogera les moteurs de recherche IA pour mesurer comment
              ils parlent de votre marque, s'ils vous citent comme expert et s'ils
              vous recommandent a vos clients potentiels.
            </Typography>
            {!activeBrand?.strategy && (
              <Alert severity="warning">
                Configurez la Brand Knowledge pour de meilleurs resultats. Sans strategie,
                l'agent ne pourra pas generer les bonnes questions.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? <CircularProgress size={20} /> : "Lancer l'audit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Detail view ──

function BrandPerceptionDetail({
  audit: initialAudit,
  onBack,
  onRefresh,
}: {
  audit: Audit;
  onBack: () => void;
  onRefresh: () => Promise<void>;
}) {
  const { activeBrand, refreshBrands } = useBrand();
  const [audit, setAudit] = useState(initialAudit);
  const [summary, setSummary] = useState<BrandPerceptionSummary | null>(
    initialAudit.brandPerceptionSummary || null,
  );
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState(0);
  const [selectedZone, setSelectedZone] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState('');
  const [enhancing, setEnhancing] = useState<string | null>(null);
  const [enhancePreview, setEnhancePreview] = useState<{
    query: string;
    axis: string;
    current: BrandStrategy;
    suggested: BrandStrategy;
    explanation: string;
  } | null>(null);
  const [applyingStrategy, setApplyingStrategy] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [auditData, summaryData, resultsData] = await Promise.all([
        auditsApi.getById(audit.auditId),
        auditsApi.getSummary(audit.auditId).catch(() => null),
        auditsApi.getResults(audit.auditId, 'brand_perception').catch(() => []),
      ]);
      if (auditData) {
        setAudit(auditData);
        setSummary(
          auditData.brandPerceptionSummary ||
          (summaryData as BrandPerceptionSummary | null) ||
          null,
        );
      }
      setResults(resultsData);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [audit.auditId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (summary?.referral?.zones && !selectedZone) {
      const zones = Object.keys(summary.referral.zones);
      if (zones.length > 0) setSelectedZone(zones[0]);
    }
  }, [summary, selectedZone]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    await onRefresh();
    setRefreshing(false);
  };

  const handleEnhanceStrategy = async (query: string, response: string, axis: string) => {
    if (!activeBrand?.brandId) return;
    const key = `${axis}:${query}`;
    setEnhancing(key);
    try {
      const res = await api.post<{
        success: boolean;
        explanation: string;
        currentStrategy: BrandStrategy;
        suggestedStrategy: BrandStrategy;
      }>(`/brands/${activeBrand.brandId}/strategy/suggest`, {
        query,
        response,
        axis,
        auditId: audit.auditId,
      });
      setEnhancePreview({
        query,
        axis,
        current: res.currentStrategy || {},
        suggested: res.suggestedStrategy || {},
        explanation: res.explanation || '',
      });
    } catch {
      // ignore
    } finally {
      setEnhancing(null);
    }
  };

  const handleApplyEnhancement = async () => {
    if (!activeBrand?.brandId || !enhancePreview) return;
    setApplyingStrategy(true);
    try {
      await api.patch(
        `/brands/${activeBrand.brandId}/strategy`,
        enhancePreview.suggested,
      );
      setFeedbackSuccess(enhancePreview.query);
      setTimeout(() => setFeedbackSuccess(''), 3000);
      await refreshBrands();
      setEnhancePreview(null);
    } catch {
      // ignore
    } finally {
      setApplyingStrategy(false);
    }
  };

  const agentStatus = audit.agentStatuses?.brand_perception || 'pending';

  const narrativeResults = results.filter((r) => r.axis === 'narrative');
  const authorityResults = results.filter((r) => r.axis === 'authority');
  const referralResults = results.filter((r) => r.axis === 'referral');
  const zoneResults = selectedZone
    ? referralResults.filter((r) => r.zone === selectedZone)
    : referralResults;

  const zones = summary?.referral?.zones ? Object.keys(summary.referral.zones) : [];

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
        Retour
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          Brand Perception — {new Date(audit.createdAt).toLocaleDateString('fr-FR')}
        </Typography>
        <Chip
          label={STATUS_LABELS[audit.status] || audit.status}
          color={STATUS_COLORS[audit.status] || 'default'}
        />
        <Chip
          label={`Agent: ${STATUS_LABELS[agentStatus] || agentStatus}`}
          size="small"
          sx={{
            bgcolor: `${AGENT_STATUS_COLORS[agentStatus]}20`,
            color: AGENT_STATUS_COLORS[agentStatus],
            fontWeight: 600,
          }}
        />
        <Tooltip title="Rafraichir">
          <IconButton onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {audit.status === 'running' && <LinearProgress sx={{ mb: 3, borderRadius: 1 }} />}

      {audit.status === 'pending' && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography color="text.secondary">
              Audit en attente de traitement...
            </Typography>
          </CardContent>
        </Card>
      )}

      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <SummaryCard title="Score Global" value={`${summary.overall_brand_perception_score}`} suffix="/100" color="#7C3AED" />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <SummaryCard title="Narrative" value={`${summary.narrative_score}`} suffix="/100" color="#8B5CF6" />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <SummaryCard title="Autorite" value={`${summary.authority_score}`} suffix="/100" color="#00DDB3" />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <SummaryCard title="Referral" value={`${summary.referral_score}`} suffix="/100" color="#FF6B35" />
          </Grid>
        </Grid>
      )}

      {feedbackSuccess && (
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
          Strategie mise a jour avec succes.
        </Alert>
      )}

      {(summary || results.length > 0) && (
        <>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ mb: 3, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}
          >
            <Tab label="Narrative" />
            <Tab label="Autorite thematique" />
            <Tab label="Referral / Visibilite" />
          </Tabs>

          {/* Narrative tab */}
          {tab === 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Ce que les moteurs IA disent de votre marque quand on les interroge directement.
                L'alignement mesure si la perception IA correspond a votre strategie de marque.
              </Typography>
              {summary?.narrative && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Typography variant="h6" fontWeight={600}>
                        Perception IA
                      </Typography>
                      <Chip
                        label={`Alignement : ${ALIGNMENT_LABELS[summary.narrative.alignment] || summary.narrative.alignment}`}
                        size="small"
                        sx={{
                          bgcolor: `${ALIGNMENT_COLORS[summary.narrative.alignment] || '#888'}20`,
                          color: ALIGNMENT_COLORS[summary.narrative.alignment] || '#888',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {summary.narrative.key_perception}
                    </Typography>
                    {summary.narrative.pros?.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">Points forts</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {summary.narrative.pros.map((p, i) => (
                            <Chip key={i} label={p} size="small" sx={{ bgcolor: '#00DDB320', color: '#00DDB3' }} />
                          ))}
                        </Box>
                      </Box>
                    )}
                    {summary.narrative.cons?.length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Points faibles</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {summary.narrative.cons.map((c, i) => (
                            <Chip key={i} label={c} size="small" sx={{ bgcolor: '#FF444420', color: '#FF4444' }} />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}
              <QueryResultsList
                results={narrativeResults}
                loading={loading}
                brandName={activeBrand?.name}
                brandUrl={activeBrand?.url}
                aliases={activeBrand?.strategy?.aliases}
                onEnhance={(q, r) => handleEnhanceStrategy(q, r, 'narrative')}
                enhancingKey={enhancing}
              />
            </Box>
          )}

          {/* Authority tab */}
          {tab === 1 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Quand un utilisateur pose une question educative dans votre domaine (« comment faire... »,
                « quelles sont les bonnes pratiques... »), votre site est-il utilise comme SOURCE
                par le moteur IA ? L'autorite thematique mesure si votre contenu fait reference —
                pas seulement si votre nom apparait dans la reponse, mais si votre domaine est cite
                parmi les sources.
              </Typography>
              {summary?.authority && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Typography variant="h6" fontWeight={600}>
                        Autorite thematique
                      </Typography>
                      <Chip
                        label={`Source dans ${Math.round((summary.authority.source_rate ?? summary.authority.citation_rate) * 100)}% des requetes`}
                        size="small"
                        sx={{
                          bgcolor: (summary.authority.source_rate ?? summary.authority.citation_rate) > 0 ? '#00DDB320' : '#FF444420',
                          color: (summary.authority.source_rate ?? summary.authority.citation_rate) > 0 ? '#00DDB3' : '#FF4444',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                    {summary.authority.cited_topics?.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">Topics ou vous etes utilise comme source</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {summary.authority.cited_topics.map((t, i) => (
                            <Chip key={i} label={t} size="small" color="success" />
                          ))}
                        </Box>
                      </Box>
                    )}
                    {summary.authority.missing_topics?.length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Topics ou vous n'etes pas source</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {summary.authority.missing_topics.map((t, i) => (
                            <Chip key={i} label={t} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}
              <QueryResultsList
                results={authorityResults}
                loading={loading}
                brandName={activeBrand?.name}
                brandUrl={activeBrand?.url}
                aliases={activeBrand?.strategy?.aliases}
                onEnhance={(q, r) => handleEnhanceStrategy(q, r, 'authority')}
                enhancingKey={enhancing}
              />
            </Box>
          )}

          {/* Referral tab */}
          {tab === 2 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Les moteurs IA vous recommandent-ils quand un client potentiel cherche une solution ?
                L'echelle de visibilite teste 5 niveaux de requetes — du plus generique (L1)
                au plus specifique a votre strategie (L5). Aucune requete ne mentionne votre nom.
              </Typography>
              {zones.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Typography variant="body2" color="text.secondary">Zone :</Typography>
                  <Select
                    value={selectedZone}
                    onChange={(e) => setSelectedZone(e.target.value)}
                    size="small"
                    sx={{ minWidth: 200 }}
                  >
                    {zones.map((z) => (
                      <MenuItem key={z} value={z}>{z}</MenuItem>
                    ))}
                  </Select>
                </Box>
              )}

              {selectedZone && summary?.referral?.zones?.[selectedZone] && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                      Echelle de visibilite — {selectedZone}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Score zone : {summary.referral.zones[selectedZone].score}/100
                    </Typography>
                    {(['L1', 'L2', 'L3', 'L4', 'L5'] as const).map((level) => {
                      const data = summary.referral.zones[selectedZone]?.levels?.[level];
                      if (!data) return null;
                      return (
                        <Box key={level} sx={{ mb: 2 }}>
                          <ScoreBar
                            score={data.score}
                            label={`${LEVEL_LABELS[level]} (${data.cited}/${data.total} cite${data.cited > 1 ? 's' : ''})`}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            Poids : {Math.round(LEVEL_WEIGHTS[level] * 100)}%
                          </Typography>
                        </Box>
                      );
                    })}
                  </CardContent>
                </Card>
              )}

              <QueryResultsList
                results={zoneResults}
                loading={loading}
                brandName={activeBrand?.name}
                brandUrl={activeBrand?.url}
                aliases={activeBrand?.strategy?.aliases}
                onEnhance={(q, r) => handleEnhanceStrategy(q, r, 'referral')}
                enhancingKey={enhancing}
              />
            </Box>
          )}
        </>
      )}

      {/* Recommendations */}
      {summary?.recommendations && summary.recommendations.length > 0 && (
        <Card sx={{ mb: 3, mt: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Recommandations
            </Typography>
            <List dense disablePadding>
              {summary.recommendations.map((rec, i) => (
                <BrandPerceptionRecItem
                  key={i}
                  rec={rec}
                  auditId={audit.auditId}
                  brandId={activeBrand?.brandId}
                />
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Feedback loop — suggestions */}
      {summary && (
        (summary.suggested_scenarios?.length > 0 || summary.suggested_keywords?.length > 0) && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                Enrichir votre strategie
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Ces suggestions sont basees sur les lacunes detectees lors de l'audit.
                Ajoutez-les a votre Brand Knowledge pour ameliorer les prochains audits.
              </Typography>

              {summary.suggested_scenarios?.map((s, i) => (
                <SuggestionRow
                  key={`s-${i}`}
                  label="Scenario"
                  value={s}
                  color="#7C3AED"
                  alreadyAdded={activeBrand?.strategy?.scenarios?.includes(s) || false}
                  onAdd={() => {
                    const scenarios = [...(activeBrand?.strategy?.scenarios || []), s];
                    api.patch(`/brands/${activeBrand!.brandId}/strategy`, { scenarios }).then(() => {
                      setFeedbackSuccess(s);
                      setTimeout(() => setFeedbackSuccess(''), 3000);
                      refreshBrands();
                    });
                  }}
                  onRemove={() => {
                    const scenarios = (activeBrand?.strategy?.scenarios || []).filter((x) => x !== s);
                    api.patch(`/brands/${activeBrand!.brandId}/strategy`, { scenarios }).then(() => {
                      setFeedbackSuccess(`"${s}" retire`);
                      setTimeout(() => setFeedbackSuccess(''), 3000);
                      refreshBrands();
                    });
                  }}
                />
              ))}

              {summary.suggested_keywords?.map((k, i) => (
                <SuggestionRow
                  key={`k-${i}`}
                  label="Keyword"
                  value={k}
                  color="#00DDB3"
                  alreadyAdded={activeBrand?.strategy?.keywords?.includes(k) || false}
                  onAdd={() => {
                    const keywords = [...(activeBrand?.strategy?.keywords || []), k];
                    api.patch(`/brands/${activeBrand!.brandId}/strategy`, { keywords }).then(() => {
                      setFeedbackSuccess(k);
                      setTimeout(() => setFeedbackSuccess(''), 3000);
                      refreshBrands();
                    });
                  }}
                  onRemove={() => {
                    const keywords = (activeBrand?.strategy?.keywords || []).filter((x) => x !== k);
                    api.patch(`/brands/${activeBrand!.brandId}/strategy`, { keywords }).then(() => {
                      setFeedbackSuccess(`"${k}" retire`);
                      setTimeout(() => setFeedbackSuccess(''), 3000);
                      refreshBrands();
                    });
                  }}
                />
              ))}
            </CardContent>
          </Card>
        )
      )}

      <StrategyDiffDialog
        preview={enhancePreview}
        applying={applyingStrategy}
        onClose={() => setEnhancePreview(null)}
        onConfirm={handleApplyEnhancement}
      />
    </Box>
  );
}

const STRATEGY_FIELD_LABELS: Record<keyof BrandStrategy, string> = {
  aliases: 'Aliases',
  industry: 'Industrie',
  clientPersonas: 'Personas client',
  businessModel: 'Modele business',
  geographicFocus: 'Zone geographique',
  description: 'Description',
  differentiators: 'Differenciateurs',
  scenarios: 'Scenarios',
  keywords: 'Mots-cles',
  customerQuestions: 'Questions avant achat',
  customerObjections: 'Objections des prospects',
  avoidTopics: 'Sujets à éviter',
  targetLanguages: 'Langues cibles',
  envergure: 'Envergure',
  brandMaturity: 'Maturité (obsolète)',
  existingAssets: 'Actifs de notoriété',
};

const STRATEGY_ARRAY_FIELDS: (keyof BrandStrategy)[] = [
  'keywords',
  'scenarios',
  'differentiators',
  'aliases',
];
const STRATEGY_SCALAR_FIELDS: (keyof BrandStrategy)[] = [
  'industry',
  'description',
  'clientPersonas',
  'businessModel',
  'geographicFocus',
];

function diffArrays(a: string[] = [], b: string[] = []) {
  const aSet = new Set(a);
  const bSet = new Set(b);
  return {
    added: b.filter((x) => !aSet.has(x)),
    removed: a.filter((x) => !bSet.has(x)),
  };
}

type DiffToken = { type: 'eq' | 'add' | 'del'; text: string };

// Tokenize keeping whitespace and punctuation as separate tokens so we can
// reassemble the diff without breaking spacing.
function tokenizeWords(s: string): string[] {
  return s.match(/\s+|[^\s]+/g) || [];
}

// LCS-based word diff. Small strategy fields, so O(n*m) is fine.
function wordDiff(before: string, after: string): DiffToken[] {
  const a = tokenizeWords(before);
  const b = tokenizeWords(after);
  const n = a.length;
  const m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out: DiffToken[] = [];
  let i = 0;
  let j = 0;
  const push = (type: DiffToken['type'], text: string) => {
    const last = out[out.length - 1];
    if (last && last.type === type) last.text += text;
    else out.push({ type, text });
  };
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      push('eq', a[i]);
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      push('del', a[i]);
      i++;
    } else {
      push('add', b[j]);
      j++;
    }
  }
  while (i < n) push('del', a[i++]);
  while (j < m) push('add', b[j++]);
  return out;
}

function InlineWordDiff({ before, after }: { before: string; after: string }) {
  const tokens = useMemo(() => wordDiff(before, after), [before, after]);
  return (
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
      {tokens.map((t, i) => {
        if (t.type === 'eq') {
          return (
            <span key={i} style={{ color: 'rgba(11,11,15,0.55)' }}>
              {t.text}
            </span>
          );
        }
        if (t.type === 'add') {
          return (
            <span
              key={i}
              style={{
                backgroundColor: '#00DDB330',
                color: '#00DDB3',
                fontWeight: 600,
                borderRadius: 2,
                padding: '0 2px',
              }}
            >
              {t.text}
            </span>
          );
        }
        return (
          <span
            key={i}
            style={{
              backgroundColor: '#FF444420',
              color: '#FF4444',
              textDecoration: 'line-through',
              borderRadius: 2,
              padding: '0 2px',
            }}
          >
            {t.text}
          </span>
        );
      })}
    </Typography>
  );
}

function StrategyDiffDialog({
  preview,
  applying,
  onClose,
  onConfirm,
}: {
  preview: {
    query: string;
    axis: string;
    current: BrandStrategy;
    suggested: BrandStrategy;
    explanation: string;
  } | null;
  applying: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const hasChanges = useMemo(() => {
    if (!preview) return false;
    for (const f of STRATEGY_ARRAY_FIELDS) {
      const { added, removed } = diffArrays(
        (preview.current[f] as string[]) || [],
        (preview.suggested[f] as string[]) || [],
      );
      if (added.length || removed.length) return true;
    }
    for (const f of STRATEGY_SCALAR_FIELDS) {
      const before = (preview.current[f] as string) || '';
      const after = (preview.suggested[f] as string) || '';
      if (before !== after && after) return true;
    }
    return false;
  }, [preview]);

  return (
    <Dialog open={!!preview} onClose={applying ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUpIcon sx={{ color: '#7C3AED' }} />
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Mise a jour de la Brand Knowledge
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Pour mieux apparaitre sur : « {preview?.query} »
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {preview?.explanation && hasChanges && (
          <Alert severity="info" icon={<LightbulbIcon />} sx={{ mb: 2 }}>
            {preview.explanation}
          </Alert>
        )}

        {!hasChanges && preview && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Aucun changement a appliquer : votre strategie couvre deja cette requete.
          </Alert>
        )}

        {preview && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {STRATEGY_ARRAY_FIELDS.map((field) => {
              const { added, removed } = diffArrays(
                (preview.current[field] as string[]) || [],
                (preview.suggested[field] as string[]) || [],
              );
              if (!added.length && !removed.length) return null;
              return (
                <Box key={field}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    {STRATEGY_FIELD_LABELS[field]}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {added.map((v) => (
                      <Chip
                        key={`a-${v}`}
                        label={`+ ${v}`}
                        size="small"
                        sx={{ bgcolor: '#00DDB320', color: '#00DDB3', fontWeight: 600 }}
                      />
                    ))}
                    {removed.map((v) => (
                      <Chip
                        key={`r-${v}`}
                        label={`− ${v}`}
                        size="small"
                        sx={{
                          bgcolor: '#FF444420',
                          color: '#FF4444',
                          fontWeight: 600,
                          textDecoration: 'line-through',
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              );
            })}

            {STRATEGY_SCALAR_FIELDS.map((field) => {
              const before = (preview.current[field] as string) || '';
              const after = (preview.suggested[field] as string) || '';
              if (before === after || !after) return null;
              return (
                <Box key={field}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    {STRATEGY_FIELD_LABELS[field]}
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: 'rgba(11,11,15,0.03)',
                      borderRadius: 0.5,
                      p: 1,
                    }}
                  >
                    <InlineWordDiff before={before} after={after} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={applying}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={applying || !hasChanges}
          startIcon={applying ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {applying ? 'Application...' : 'Appliquer les changements'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function BrandPerceptionRecItem({ rec, auditId, brandId }: { rec: string; auditId: string; brandId?: string }) {
  const { tenant } = useAuth();
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const tasksEnabled = !!tenant?.settings?.featureFlags?.tasksEnabled;

  const handleAdd = async () => {
    if (added || adding) return;
    setAdding(true);
    try {
      await tasksApi.create({
        title: rec.slice(0, 120),
        description: rec,
        category: 'external',
        sourceType: 'audit_brand',
        auditId,
        brandId,
      });
      setAdded(true);
    } catch { /* ignore */ } finally {
      setAdding(false);
    }
  };

  return (
    <ListItem disableGutters>
      <ListItemIcon sx={{ minWidth: 32 }}>
        <LightbulbIcon fontSize="small" sx={{ color: '#FF6B35' }} />
      </ListItemIcon>
      <ListItemText primary={rec} primaryTypographyProps={{ variant: 'body2' }} />
      {tasksEnabled && (
        <Tooltip title={added ? 'Ajoute aux taches' : 'Ajouter aux taches'}>
          <IconButton size="small" onClick={handleAdd} disabled={adding} sx={{ ml: 0.5 }}>
            {adding ? (
              <CircularProgress size={14} />
            ) : added ? (
              <CheckCircleOutlineIcon fontSize="small" sx={{ color: '#00DDB3' }} />
            ) : (
              <AddTaskIcon fontSize="small" sx={{ color: '#7C3AED' }} />
            )}
          </IconButton>
        </Tooltip>
      )}
    </ListItem>
  );
}

// ── Query results table with expandable rows ──

function QueryResultsList({
  results,
  loading,
  brandName,
  brandUrl,
  aliases,
  onEnhance,
  enhancingKey,
}: {
  results: any[];
  loading: boolean;
  brandName?: string;
  brandUrl?: string;
  aliases?: string[];
  onEnhance: (query: string, response: string) => void;
  enhancingKey: string | null;
}) {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (results.length === 0) return null;

  const hasLevels = results.some((r) => r.level);

  return (
    <Card>
      <CardContent sx={{ pb: 0 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
          Requetes ({results.length})
        </Typography>
      </CardContent>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>Requete</TableCell>
              <TableCell>Moteur</TableCell>
              {hasLevels && <TableCell>Niveau</TableCell>}
              <TableCell>Cite</TableCell>
              <TableCell>Sources</TableCell>
              <TableCell>Recommande</TableCell>
              <TableCell>Sentiment</TableCell>
              <TableCell>Score</TableCell>
              <TableCell width={40} />
            </TableRow>
          </TableHead>
          <TableBody>
            {results.map((r: any, i: number) => {
              const rowKey = r.SK || `${i}`;
              const isExpanded = expandedRow === rowKey;
              const enhanceKey = `${r.axis}:${r.query}`;
              return (
                <>
                  <TableRow key={rowKey} hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => setExpandedRow(isExpanded ? null : rowKey)}>
                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {r.query}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={r.engine || 'perplexity'} size="small" variant="outlined" />
                    </TableCell>
                    {hasLevels && (
                      <TableCell>
                        {r.level ? <Chip label={r.level} size="small" /> : '—'}
                      </TableCell>
                    )}
                    <TableCell>
                      {r.cited ? (
                        <Chip label="Oui" size="small" color="success" variant="outlined" />
                      ) : (
                        <Chip label="Non" size="small" color="error" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      {r.sources?.length > 0 ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" fontWeight={600}>{r.sources.length}</Typography>
                          {r.brandIsSource && (
                            <Chip label="vous" size="small" sx={{ bgcolor: '#00DDB320', color: '#00DDB3', fontWeight: 700, height: 20, fontSize: '0.7rem' }} />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">0</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.recommended ? (
                        <Chip label="Oui" size="small" color="success" variant="outlined" />
                      ) : (
                        <Chip label="Non" size="small" color="error" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={r.sentiment}
                        size="small"
                        sx={{
                          bgcolor: r.sentiment === 'positive' ? '#00DDB320' :
                                   r.sentiment === 'negative' ? '#FF444420' : '#8B9DC320',
                          color: r.sentiment === 'positive' ? '#00DDB3' :
                                 r.sentiment === 'negative' ? '#FF4444' : '#8B9DC3',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        fontWeight={700}
                        sx={{
                          color: r.score >= 70 ? '#00DDB3' : r.score >= 40 ? '#FF6B35' : '#FF4444',
                        }}
                      >
                        {r.score}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Apparaitre davantage sur cette requete">
                        <IconButton
                          size="small"
                          onClick={() => onEnhance(r.query, r.response || '')}
                          disabled={enhancingKey === enhanceKey}
                        >
                          {enhancingKey === enhanceKey ? (
                            <CircularProgress size={16} />
                          ) : (
                            <TrendingUpIcon fontSize="small" sx={{ color: '#7C3AED' }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow key={`${rowKey}-expand`}>
                    <TableCell colSpan={99} sx={{ py: 0, border: isExpanded ? undefined : 'none' }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            Reponse complete — {r.engine || 'perplexity'}
                          </Typography>
                          <Box sx={{ bgcolor: 'rgba(11,11,15,0.03)', borderRadius: 1, p: 2 }}>
                            <HighlightedResponse
                              text={r.response || 'Pas de reponse enregistree.'}
                              brandName={brandName}
                              aliases={aliases}
                            />
                          </Box>
                          {r.sources && r.sources.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                Sources ({r.sources.length})
                                {r.brandIsSource && (
                                  <Chip label="Votre site est source" size="small" color="success" sx={{ ml: 1 }} />
                                )}
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {r.sources.map((url: string, si: number) => {
                                  const isBrand = brandName && url.toLowerCase().includes(
                                    (brandUrl || brandName).replace(/^https?:\/\//, '').replace(/\/.*$/, '').toLowerCase(),
                                  );
                                  return (
                                    <Chip
                                      key={si}
                                      label={url.replace(/^https?:\/\//, '').slice(0, 50)}
                                      size="small"
                                      variant={isBrand ? 'filled' : 'outlined'}
                                      sx={isBrand ? { bgcolor: '#00DDB320', color: '#00DDB3', fontWeight: 700 } : {}}
                                      onClick={() => window.open(url, '_blank')}
                                    />
                                  );
                                })}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}
